import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchPlacements, deletePlacement } from '../placementActions';
import { fetchAdList } from '../adActions';
import { useIsMounted } from '../../../shared/hooks';
import { APP_ROUTES, UI_MESSAGES } from '../../../shared/constants';
import { formatDate } from '../../../shared/utils/helpers';
import { extractContractError, formatErrorForDisplay } from  '@bafkal/ui-shared/utils';
import { Table, Card, Button, LoadingSpinner, ErrorMessage } from '@bafkal/ui-shared/components';
import { colors } from '@bafkal/ui-shared/tokens';

const PlacementList = () => {
  const dispatch = useDispatch();
  const { loading, error, placements, total, page, pageSize } = useSelector(
    (state) => state.placements
  );
  const ads = useSelector((state) => state.ads.ads);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const isMounted = useIsMounted();

  useEffect(() => {
    dispatch(fetchPlacements(page)).catch(() => {});
  }, [dispatch, page]);

  useEffect(() => {
    if (ads.length === 0) {
      dispatch(fetchAdList()).catch(() => {});
    }
  }, [dispatch, ads.length]);

  const getAdUnitName = (adUnitId) => {
    const ad = ads.find((a) => a.id === adUnitId);
    return ad ? ad.name : adUnitId || '—';
  };

  const summarizeTargeting = (rules) => {
    if (!rules || typeof rules !== 'object') return '—';
    const keys = Object.keys(rules);
    if (keys.length === 0) return '—';
    return keys
      .slice(0, 2)
      .map((k) => {
        const v = Array.isArray(rules[k]) ? rules[k].join(', ') : rules[k];
        return `${k}: ${v}`;
      })
      .join(' | ') + (keys.length > 2 ? ` +${keys.length - 2} more` : '');
  };

  const handlePageChange = (newPage) => {
    dispatch(fetchPlacements(newPage));
  };

  const handleDelete = async (id) => {
    if (!window.confirm(UI_MESSAGES.PLACEMENT.DELETE_CONFIRM)) return;
    setDeleteLoading(id);
    try {
      await dispatch(deletePlacement(id));
      await dispatch(fetchPlacements(page));
    } catch (err) {
      const contractError = extractContractError(err, UI_MESSAGES.PLACEMENT.DELETE_FAILED);
      alert(formatErrorForDisplay(contractError));
    } finally {
      if (isMounted.current) setDeleteLoading(null);
    }
  };

  const loadData = () => dispatch(fetchPlacements(page));

  if (loading && placements.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  if (error) {
    return <ErrorMessage message={error.message || UI_MESSAGES.GENERIC.ERROR} onRetry={loadData} />;
  }

  if (placements.length === 0) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '32px 0' }}>
          <svg style={{ margin: '0 auto 16px', width: '48px', height: '48px', color: colors.neutral[400] }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 style={{ fontWeight: 500, marginBottom: '4px' }}>No placements</h3>
          <p style={{ fontSize: '0.875rem', color: colors.neutral[500], marginBottom: '16px' }}>Get started by creating a new placement.</p>
          <Link
            to={APP_ROUTES.INVENTORY.PLACEMENT_NEW}
            className="bafkal-btn bafkal-btn-primary"
            style={{ display: 'inline-flex', alignItems: 'center', padding: '8px 16px', borderRadius: '6px', textDecoration: 'none', fontSize: '0.875rem', fontWeight: 500 }}
          >
            Create your first placement
          </Link>
        </div>
      </Card>
    );
  }

  // Column definitions for the shared Table component
  const columns = [
    {
      key: 'adUnitId',
      header: 'Ad Unit',
      render: (_, p) => (
        <div>
          <div style={{ fontWeight: 500 }}>{getAdUnitName(p.adUnitId)}</div>
          <div style={{ fontSize: '0.75rem', color: colors.neutral[500] }}>ID: {p.id}</div>
        </div>
      ),
    },
    {
      key: 'targetUrl',
      header: 'Target URL',
      render: (_, p) => <span style={{ maxWidth: '200px', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.targetUrl || '—'}</span>,
    },
    {
      key: 'targetingRules',
      header: 'Targeting Rules',
      render: (_, p) => <span style={{ maxWidth: '200px', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{summarizeTargeting(p.targetingRules)}</span>,
    },
    {
      key: 'createdAt',
      header: 'Created',
      render: (_, p) => formatDate(p.createdAt),
    },
    {
      key: 'actions',
      header: '',
      render: (_, p) => (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <Link to={APP_ROUTES.INVENTORY.PLACEMENT_EDIT(p.id)} style={{ color: colors.primary[600], fontSize: '0.875rem', fontWeight: 500, textDecoration: 'none' }}>
            Edit
          </Link>
          <Button
            variant="danger"
            size="sm"
            disabled={deleteLoading === p.id}
            onClick={() => handleDelete(p.id)}
          >
            {deleteLoading === p.id ? 'Deleting…' : 'Delete'}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      {/* Mobile card view */}
      <div className="sm:hidden space-y-4 mb-4">
        {placements.map((p) => (
          <Card key={p.id}>
            <div style={{ fontWeight: 500, marginBottom: '4px' }}>{getAdUnitName(p.adUnitId)}</div>
            <div style={{ fontSize: '0.75rem', color: colors.neutral[500], marginBottom: '4px', wordBreak: 'break-all' }}>{p.targetUrl || '—'}</div>
            <div style={{ fontSize: '0.75rem', color: colors.neutral[400], marginBottom: '4px' }}>{summarizeTargeting(p.targetingRules)}</div>
            <div style={{ fontSize: '0.75rem', color: colors.neutral[400], marginBottom: '12px' }}>{formatDate(p.createdAt)}</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', borderTop: `1px solid ${colors.neutral[100]}`, paddingTop: '12px' }}>
              <Link to={APP_ROUTES.INVENTORY.PLACEMENT_EDIT(p.id)} style={{ color: colors.primary[600], fontSize: '0.875rem', fontWeight: 500, textDecoration: 'none' }}>Edit</Link>
              <Button variant="danger" size="sm" disabled={deleteLoading === p.id} onClick={() => handleDelete(p.id)}>
                {deleteLoading === p.id ? 'Deleting…' : 'Delete'}
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {/* Desktop table view */}
      <div className="hidden sm:block">
        <Table columns={columns} data={placements} keyField="id" emptyMessage="No placements found." />
      </div>

      {/* Pagination */}
      {total > pageSize && (
        <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: 'var(--bafkal-bg-surface)', border: `1px solid ${colors.neutral[200]}`, borderRadius: '8px', padding: '12px 16px' }}>
          <div style={{ fontSize: '0.875rem', color: colors.neutral[700] }}>
            Showing <strong>{(page - 1) * pageSize + 1}</strong>–<strong>{Math.min(page * pageSize, total)}</strong> of <strong>{total}</strong> placements
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button variant="secondary" disabled={page <= 1 || loading} onClick={() => handlePageChange(page - 1)}>Previous</Button>
            <Button variant="secondary" disabled={page * pageSize >= total || loading} onClick={() => handlePageChange(page + 1)}>Next</Button>
          </div>
        </div>
      )}
    </>
  );
};

export default PlacementList;
